import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Button } from '@voilajsx/uikit/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Badge } from '@voilajsx/uikit/badge';
import { Info } from 'lucide-react';

const Components = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Understanding Components</h1>
          <p className="text-lg text-muted-foreground">
            Learn how UIKit components work and how to use them effectively.
          </p>
        </div>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Component Categories</h2>
          <p className="text-muted-foreground">
            UIKit organizes components into logical categories:
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Form Components</CardTitle>
                <CardDescription>User input and data collection</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">Button</Badge>
                  <Badge variant="secondary">Input</Badge>
                  <Badge variant="secondary">Textarea</Badge>
                  <Badge variant="secondary">Select</Badge>
                  <Badge variant="secondary">Checkbox</Badge>
                  <Badge variant="secondary">Switch</Badge>
                  <Badge variant="secondary">Slider</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Display Components</CardTitle>
                <CardDescription>Content presentation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">Card</Badge>
                  <Badge variant="secondary">Badge</Badge>
                  <Badge variant="secondary">Avatar</Badge>
                  <Badge variant="secondary">Table</Badge>
                  <Badge variant="secondary">Progress</Badge>
                  <Badge variant="secondary">Skeleton</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Overlay Components</CardTitle>
                <CardDescription>Modals and popups</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">Dialog</Badge>
                  <Badge variant="secondary">Sheet</Badge>
                  <Badge variant="secondary">Popover</Badge>
                  <Badge variant="secondary">Tooltip</Badge>
                  <Badge variant="secondary">DropdownMenu</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Feedback Components</CardTitle>
                <CardDescription>User notifications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">Alert</Badge>
                  <Badge variant="secondary">Toast</Badge>
                  <Badge variant="secondary">Sonner</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Import Pattern</h2>
          <p className="text-muted-foreground">
            Each component is imported from its own subpath for optimal tree-shaking:
          </p>

          <CodePreview
            title="Component Imports"
            code={`// Each component from its own path
import { Button } from '@voilajsx/uikit/button';
import { Card, CardContent } from '@voilajsx/uikit/card';
import { Input } from '@voilajsx/uikit/input';
import { Dialog, DialogContent } from '@voilajsx/uikit/dialog';

// NOT like this (barrel import)
// import { Button, Card, Input } from '@voilajsx/uikit';`}
            language="tsx"
          />

          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription>
              Individual imports ensure smaller bundle sizes. Only the components you use are included in your build.
            </AlertDescription>
          </Alert>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Variant Pattern</h2>
          <p className="text-muted-foreground">
            Most components support variants for different visual styles:
          </p>

          <CodePreview
            title="Button Variants"
            code={`import { Button } from '@voilajsx/uikit/button';

// Visual variants
<Button variant="default">Default</Button>
<Button variant="secondary">Secondary</Button>
<Button variant="outline">Outline</Button>
<Button variant="ghost">Ghost</Button>
<Button variant="link">Link</Button>
<Button variant="destructive">Destructive</Button>

// Size variants
<Button size="sm">Small</Button>
<Button size="default">Default</Button>
<Button size="lg">Large</Button>
<Button size="icon">🔔</Button>`}
            language="tsx"
          />

          <div className="flex flex-wrap gap-2 p-4 bg-muted/30 rounded-lg">
            <Button variant="default">Default</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Compound Components</h2>
          <p className="text-muted-foreground">
            Some components use a compound pattern with multiple sub-components:
          </p>

          <CodePreview
            title="Card Example"
            code={`import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
} from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';

<Card>
  <CardHeader>
    <CardTitle>Card Title</CardTitle>
    <CardDescription>Card description goes here</CardDescription>
  </CardHeader>
  <CardContent>
    <p>Main content of the card.</p>
  </CardContent>
  <CardFooter>
    <Button>Action</Button>
  </CardFooter>
</Card>`}
            language="tsx"
          />

          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Card Title</CardTitle>
              <CardDescription>Card description goes here</CardDescription>
            </CardHeader>
            <CardContent>
              <p>Main content of the card.</p>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Styling Components</h2>
          <p className="text-muted-foreground">
            Components accept <code className="bg-muted px-1 rounded">className</code> for additional Tailwind classes:
          </p>

          <CodePreview
            title="Custom Styling"
            code={`// Add spacing, sizing, or custom styles
<Button className="w-full">Full Width Button</Button>

<Card className="max-w-md shadow-lg">
  <CardContent className="p-8">
    Custom padding
  </CardContent>
</Card>

// Combine with Tailwind utilities
<Badge className="animate-pulse">New</Badge>`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Complete Component List</h2>
          <p className="text-muted-foreground">
            All 37+ components available in UIKit:
          </p>

          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {[
                'Accordion', 'Alert', 'AlertDialog', 'AspectRatio', 'Avatar',
                'Badge', 'Button', 'Calendar', 'Card', 'Checkbox',
                'Collapsible', 'Command', 'ContextMenu', 'Dialog', 'DropdownMenu',
                'Form', 'HoverCard', 'Input', 'Label', 'Menubar',
                'NavigationMenu', 'Popover', 'Progress', 'RadioGroup', 'ScrollArea',
                'Select', 'Separator', 'Sheet', 'Skeleton', 'Slider',
                'Sonner', 'Switch', 'Table', 'Tabs', 'Textarea',
                'Toast', 'Toggle', 'Tooltip'
              ].map(c => (
                <div key={c} className="text-muted-foreground">{c}</div>
              ))}
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Next Steps</h2>
          <p className="text-muted-foreground">
            You now understand how UIKit components work. Next, we'll dive into the theming system to customize colors and appearance.
          </p>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default Components;
